/**
 * Lightweight Markdown renderer for AI/commentary output.
 *
 * Renders the LLM's raw markdown (headings, bold, lists) using
 * react-markdown with Hebrew-aware Tailwind typography classes.
 * No `rehype-raw` so we never accept embedded HTML — safer when the
 * content originates from a model.
 *
 * Drop-in usage:
 *   <Markdown>{text}</Markdown>
 *
 * The default styling matches the rest of the app: gold accents on
 * H1/H2/H3, comfortable body leading-relaxed, RTL-aligned lists.
 */
import React from "react";
import ReactMarkdown from "react-markdown";

export default function Markdown({ children, className = "", dir = "rtl" }) {
  if (!children || typeof children !== "string") return null;
  return (
    <div
      className={`markdown-rendered text-[15px] leading-relaxed text-foreground ${className}`}
      dir={dir}
    >
      <ReactMarkdown
        components={{
          h1: ({ children: c }) => (
            <h3 className="font-heading text-lg text-gold mb-2 mt-3 first:mt-0">{c}</h3>
          ),
          h2: ({ children: c }) => (
            <h3 className="font-heading text-base text-gold mb-2 mt-3 first:mt-0">{c}</h3>
          ),
          h3: ({ children: c }) => (
            <h4 className="font-heading text-base text-foreground/90 mb-1.5 mt-3 first:mt-0">{c}</h4>
          ),
          p: ({ children: c }) => (
            <p className="mb-2.5 last:mb-0 leading-relaxed">{c}</p>
          ),
          strong: ({ children: c }) => (
            <strong className="font-semibold text-gold/90">{c}</strong>
          ),
          em: ({ children: c }) => <em className="italic">{c}</em>,
          ul: ({ children: c }) => (
            <ul className="list-disc pe-5 mb-2.5 space-y-1">{c}</ul>
          ),
          ol: ({ children: c }) => (
            <ol className="list-decimal pe-5 mb-2.5 space-y-1">{c}</ol>
          ),
          li: ({ children: c }) => <li className="leading-relaxed">{c}</li>,
          blockquote: ({ children: c }) => (
            <blockquote className="border-r-2 border-gold/40 pe-3 my-2 italic text-foreground/85">
              {c}
            </blockquote>
          ),
          hr: () => <hr className="border-gold/20 my-3" />,
          code: ({ inline, children: c }) =>
            inline ? (
              <code className="px-1 py-0.5 rounded bg-card/60 text-gold/90 text-[13px]">
                {c}
              </code>
            ) : (
              <pre className="rounded bg-card/60 p-3 my-2 text-[13px] overflow-x-auto">
                <code>{c}</code>
              </pre>
            ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
